/**
 * ProtectedRoute.tsx — MER Algo Footprints
 *
 * Route guards for the public/private boundary.
 *
 * ── Guards ────────────────────────────────────────────────────────────────────
 *
 * ProtectedRoute
 *   Wraps authenticated routes (/app/*).
 *   During bootstrap ('loading'): shows a loading screen — prevents flicker.
 *   On error ('error'):           shows a retry screen — never leaves user stuck.
 *   On unauthenticated:           redirects to /login, preserving intended path.
 *   On authenticated:             renders children via <Outlet />.
 *
 * PublicOnlyRoute
 *   Wraps auth pages (/login, /signup).
 *   Authenticated users are redirected away — either to their intended destination
 *   (stored in location.state.from) or to /app.
 *   Preserves the intended-destination chain across the full login flow.
 *
 * ── Intended destination redirect ────────────────────────────────────────────
 *
 * Flow:
 *   1. User visits /app/signals (unauthenticated)
 *   2. ProtectedRoute redirects to /login with state.from = '/app/signals'
 *   3. LoginPage reads state.from and navigates there after success
 *   4. PublicOnlyRoute (if user lands on /login already authenticated) redirects
 *      to state.from or /app
 *
 * Redirect target sanitisation:
 *   Only paths starting with '/' are accepted as redirect targets.
 *   External URLs, data: URIs, and blank strings are rejected → fallback to /app.
 *
 * ── Error state ───────────────────────────────────────────────────────────────
 *
 * When session bootstrap fails after retry (network/server error), status = 'error'.
 * ProtectedRoute treats 'error' as unauthenticated (redirect to login).
 * A subtle error note is shown on the login page via location.state.authError.
 */

import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Sanitise a redirect target. Only same-origin relative paths are accepted.
 * Returns '/app' as the safe fallback.
 */
export function sanitiseRedirect(raw: string | null | undefined): string {
  if (!raw) return '/app';
  // Must start with '/' and not be '//' (protocol-relative) or a non-path character
  if (typeof raw !== 'string' || !raw.startsWith('/') || raw.startsWith('//')) return '/app';
  // Reject suspicious patterns
  if (raw.toLowerCase().startsWith('/javascript') || raw.toLowerCase().startsWith('/data')) return '/app';
  return raw;
}

// ── Loading / error screens ───────────────────────────────────────────────────

function AuthScreen({ children }: { children: React.ReactNode }) {
  return (
    <div className="mer-full-page" style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--navy, #0A1628)',
      fontFamily: 'var(--mono, monospace)',
    }}>
      {children}
    </div>
  );
}

function LoadingScreen() {
  return (
    <AuthScreen>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 28, height: 28,
          border: '2px solid rgba(26,48,80,0.8)',
          borderTopColor: 'var(--gold, #C9A84C)',
          borderRadius: '50%',
          animation: 'mer-spin 0.8s linear infinite',
        }} />
        <span style={{ color: 'var(--blue-dim, #4A6FA5)', fontSize: 11, letterSpacing: 1.5 }}>
          LOADING MER ALGO FOOTPRINTS…
        </span>
      </div>
    </AuthScreen>
  );
}

// ── ProtectedRoute ────────────────────────────────────────────────────────────

export function ProtectedRoute() {
  const { status } = useAuth();
  const location   = useLocation();

  if (status === 'loading') {
    return <LoadingScreen />;
  }

  // Both 'unauthenticated' and 'error' redirect to login.
  // 'error' additionally passes authError so login page can surface a note.
  if (status === 'unauthenticated' || status === 'error') {
    const destination = sanitiseRedirect(location.pathname);
    return (
      <Navigate
        to="/login"
        state={{
          from: destination,
          ...(status === 'error' ? { authError: true } : {}),
        }}
        replace
      />
    );
  }

  return <Outlet />;
}

// ── PublicOnlyRoute ───────────────────────────────────────────────────────────

/**
 * Redirects authenticated users away from /login and /signup.
 * Preserves the intended destination from location.state.from if present.
 */
export function PublicOnlyRoute() {
  const { status } = useAuth();
  const location   = useLocation();

  if (status === 'loading') {
    return <LoadingScreen />;
  }

  if (status === 'authenticated') {
    // If login page was reached via a ProtectedRoute redirect, honour the destination
    const intended = sanitiseRedirect((location.state as any)?.from);
    return <Navigate to={intended} replace />;
  }

  return <Outlet />;
}
